/**
 * @typedef {import("@store/reducers/notes.reducer").Note} Note
 */

/**
 * The state of the note dialog.
 * @typedef {object} NoteFormState
 * @property {string} body
 * @property {number | null} playerId
 * @property {string[]} tags
 * @property {{ attributeId: number | null, value: number | null }[]} attributeRows
 * @property {"open" | "targeted"} visibility
 * @property {string[]} audience The uuids of the people who can read a targeted note.
 */

// The backend validates every rule in this file again. The backend is the authority. Keep the texts the same.
export const LIMITS = { body: 5000, tags: 10, attributes: 10, audience: 20, label: 50 };

const TAGS_LIMIT_MESSAGE = `A note can have at most ${LIMITS.tags} tags.`;

/**
 * Trims a tag or an attribute name and turns each run of spaces into one space.
 * @param {string} raw
 * @returns {string}
 */
export const normalizeLabel = (raw) => raw.trim().replace(/ +/g, " ");

/**
 * The rule of a tag or an attribute name. The checks run in the order of the backend.
 * @param {string} raw
 * @returns {string | null} The message, or null when the label is valid.
 */
export const labelError = (raw) => {
  const label = normalizeLabel(raw);
  if ([...label].length > LIMITS.label) return `Use at most ${LIMITS.label} characters.`;
  if (!/^[A-Za-z0-9 -]*$/.test(label)) return "Use only letters, numbers, spaces, and hyphens.";
  if (!/[A-Za-z0-9]/.test(label)) return "Use at least one letter or number.";
  return null;
};

const sameLabel = (a, b) => a.toLowerCase() === b.toLowerCase();

// Tags the note dialog always offers. They exist only here until a saved note uses one.
export const SUGGESTED_TAGS = ["Progress Update", "Game", "Strength and Conditioning"];

/**
 * The tag choices of the note dialog: the suggested tags first, then the team's tags.
 * A suggestion the team already has (ignoring case) shows once, with the team's spelling.
 * @param {string[]} knownTags The tags of the team.
 * @returns {string[]}
 */
// Attributes the note dialog always offers. Unlike a tag, an attribute is a record with an id, so a suggestion is
// created on the server when someone picks it.
export const SUGGESTED_ATTRIBUTES = [{ name: "Score", min_value: 1, max_value: 10 }];

/**
 * The suggested attributes the team does not have yet (ignoring case).
 * @param {{ name: string }[]} attributes The attributes of the team.
 * @returns {{ name: string, min_value: number | null, max_value: number | null }[]}
 */
export const missingSuggestedAttributes = (attributes) =>
  SUGGESTED_ATTRIBUTES.filter((s) => !attributes.some((a) => sameLabel(a.name, s.name)));

export const withSuggestedTags = (knownTags) => [
  ...SUGGESTED_TAGS.filter((s) => !knownTags.some((t) => sameLabel(t, s))),
  ...knownTags,
];

/**
 * Adds one tag. A tag the note has already (ignoring case) is not added, and is not an error.
 * A tag that matches a known tag (ignoring case) takes the spelling of the known tag.
 * @param {string[]} tags The tags of the note.
 * @param {string} raw The text the user typed.
 * @param {string[]} knownTags The tags of the team.
 * @returns {{ tags: string[], error: string | null }}
 */
export const addTag = (tags, raw, knownTags) => {
  const label = normalizeLabel(raw);
  const error = labelError(label);
  if (error) return { tags, error };
  if (tags.some((t) => sameLabel(t, label))) return { tags, error: null };
  if (tags.length >= LIMITS.tags) return { tags, error: TAGS_LIMIT_MESSAGE };
  return { tags: [...tags, knownTags.find((t) => sameLabel(t, label)) ?? label], error: null };
};

/**
 * Applies the new value list of the tag input. A tag that is not in the list is removed. A new text is added with `addTag`.
 * @param {string[]} tags The tags of the note.
 * @param {string[]} values The value list of the input.
 * @param {string[]} knownTags The tags of the team.
 * @returns {{ tags: string[], error: string | null }} The last message of a refused tag, or null.
 */
export const applyTagSelection = (tags, values, knownTags) => {
  let next = values.filter((v) => tags.includes(v));
  let error = null;
  for (const raw of values.filter((v) => !tags.includes(v))) {
    const result = addTag(next, raw, knownTags);
    next = result.tags;
    error = result.error ?? error;
  }
  return { tags: next, error };
};

/**
 * The tags of a Visualizer column that the note does not have.
 * @param {string[]} tags
 * @param {string[]} columnTags
 * @returns {string[]}
 */
export const missingColumnTags = (tags, columnTags) => columnTags.filter((c) => !tags.some((t) => sameLabel(t, c)));

/**
 * Adds back the tags of the column. It adds as many as the limit of tags allows.
 * @param {string[]} tags
 * @param {string[]} columnTags
 * @returns {{ tags: string[], error: string | null }}
 */
export const restoreColumnTags = (tags, columnTags) => {
  let next = tags;
  let error = null;
  for (const tag of missingColumnTags(tags, columnTags)) {
    const result = addTag(next, tag, columnTags);
    next = result.tags;
    error = result.error ?? error;
  }
  return { tags: next, error };
};

const quoteList = (tags) => {
  const quoted = tags.map((t) => `“${t}”`);
  return quoted.length < 2 ? quoted[0] : `${quoted.slice(0, -1).join(", ")} and ${quoted[quoted.length - 1]}`;
};

/**
 * The warning that shows when the user removes a tag of the column.
 * @param {string[]} missing
 * @returns {string}
 */
export const columnTagsWarning = (missing) =>
  `You removed ${quoteList(missing)}. This note won't show in this column. It will still be saved.`;

const normalizeBody = (text) => text.replace(/\r\n/g, "\n").replace(/\r/g, "\n");

/**
 * The length of a body as the backend counts it: code points, after each line break becomes `\n`.
 * @param {string} text
 * @returns {number}
 */
export const bodyLength = (text) => [...normalizeBody(text)].length;

/**
 * @param {string} text
 * @returns {string | null}
 */
export const bodyError = (text) => {
  if (!text.trim()) return "A note needs some text.";
  if (bodyLength(text) > LIMITS.body) return "A note can have at most 5,000 characters.";
  return null;
};

/**
 * @param {number | null} min
 * @param {number | null} max
 * @returns {string | null}
 */
export const rangeError = (min, max) => (min != null && max != null && min > max ? "Min must not be greater than max." : null);

const valueMessage = (min, max) => {
  if (min != null && max != null) return `Enter a value from ${min} to ${max}.`;
  if (min != null) return `Enter a value of at least ${min}.`;
  if (max != null) return `Enter a value of at most ${max}.`;
  return "Enter a number.";
};

/**
 * @param {number | null} value
 * @param {{ min_value: number | null, max_value: number | null }} attribute
 * @returns {string | null}
 */
export const attributeValueError = (value, { min_value: min, max_value: max }) => {
  if (value === null || value === undefined) return "Enter a value.";
  const valid = Number.isFinite(value) && (min == null || value >= min) && (max == null || value <= max);
  return valid ? null : valueMessage(min, max);
};

/**
 * @param {"open" | "targeted"} visibility
 * @param {string[]} audience
 * @returns {string | null}
 */
export const audienceError = (visibility, audience) => {
  if (visibility !== "targeted") return null;
  if (audience.length === 0) return "Add at least one person, or choose Open.";
  if (audience.length > LIMITS.audience) return `Add at most ${LIMITS.audience} people.`;
  return null;
};

/**
 * Checks the whole form. `attributes` has one message for each attribute row, in the order of the rows.
 * @param {NoteFormState} form
 * @param {{ id: number, min_value: number | null, max_value: number | null }[]} attributeList The attributes of the team.
 * @returns {{ player: string | null, body: string | null, attributes: (string | null)[], audience: string | null, hasErrors: boolean }}
 */
export const validateNoteForm = (form, attributeList) => {
  const attributes = form.attributeRows.map(({ attributeId, value }) => {
    const attribute = attributeList.find((a) => a.id === attributeId);
    return attribute ? attributeValueError(value, attribute) : "Pick an attribute, or remove this row.";
  });
  // Every note is about a player. The UI offers no general notes.
  const player = form.playerId == null ? "Choose a player." : null;
  const body = bodyError(form.body);
  const audience = audienceError(form.visibility, form.audience);
  return { player, body, attributes, audience, hasErrors: Boolean(player || body || audience || attributes.some(Boolean)) };
};

/**
 * The body of the create and edit requests. The form must pass `validateNoteForm` first,
 * so that every attribute row has an attribute and a value.
 * @param {NoteFormState} form
 * @returns {{
 *   body: string,
 *   player_id: number | null,
 *   tags: string[],
 *   attributes: { id: number, value: number }[],
 *   visibility: "open" | "targeted",
 *   audience: string[],
 * }}
 */
export const buildNotePayload = (form) => ({
  body: normalizeBody(form.body).trim(),
  player_id: form.playerId,
  tags: form.tags,
  attributes: form.attributeRows.map(({ attributeId, value }) => ({ id: attributeId, value })),
  visibility: form.visibility,
  audience: form.visibility === "targeted" ? form.audience : [],
});

/**
 * The first state of the dialog.
 * @param {{ note?: Note, lockedPlayerId?: number, initialTags?: string[] }} options
 * @returns {NoteFormState}
 */
export const initialNoteForm = ({ note, lockedPlayerId, initialTags }) => {
  if (note) {
    return {
      body: note.body,
      playerId: note.player?.id ?? null,
      tags: [...note.tags],
      attributeRows: note.attributes.map((a) => ({ attributeId: a.id, value: a.value })),
      visibility: note.visibility,
      audience: note.audience.map((u) => u.uuid),
    };
  }
  return { body: "", playerId: lockedPlayerId ?? null, tags: [...(initialTags ?? [])], attributeRows: [], visibility: "open", audience: [] };
};

/**
 * The people a targeted note can name: the staff of the team that have a real uuid, without the signed-in user.
 * A person that is already on the note stays in the list, so the note keeps the name.
 * @param {{ uuid?: string | null, first_name: string, last_name: string }[] | undefined} coachList
 * @param {string | undefined} myUuid
 * @param {Note} [note]
 * @returns {{ value: string, label: string }[]}
 */
export const buildAudienceOptions = (coachList, myUuid, note) => {
  const options = (coachList ?? [])
    .filter((c) => c.uuid && c.uuid !== "dummy" && c.uuid !== myUuid)
    .map((c) => ({ value: c.uuid, label: `${c.first_name} ${c.last_name}`.trim() }));
  const listed = new Set(options.map((o) => o.value));
  const kept = (note?.audience ?? []).filter((u) => u.uuid !== myUuid && !listed.has(u.uuid)).map((u) => ({ value: u.uuid, label: u.name }));
  return [...options, ...kept];
};

/**
 * @param {number | null} min
 * @param {number | null} max
 * @returns {string}
 */
export const formatAttributeRange = (min, max) => (min == null && max == null ? "any number" : `${min ?? "…"}–${max ?? "…"}`);
